// Sentenzo: Lernstand ohne Verlust. Reines JavaScript, läuft im Browser und in Node (Tests: platform/sync/sync.test.mjs).
//
// Grundsatz: Jede Bewertung ist ein unveränderliches Ereignis mit einer vom Gerät erzeugten ID. Der Lernstand (FSRS-Zustand je Einheit und
// Richtung) wird aus allen Ereignissen neu berechnet. Daraus folgt:
//  - Offline lernen ist sicher: Ereignisse liegen sofort lokal, eine Warteschlange lädt sie hoch und gibt sie erst nach Bestätigung frei.
//  - Doppeltes Hochladen schadet nicht (gleiche ID, der Server ignoriert Dubletten).
//  - Mehrere Geräte: Die Ereignisse werden vereinigt und nach Zeitpunkt sortiert; alle Geräte rechnen denselben Stand aus.
//  - Ein Fehler im Planer lässt sich nachträglich beheben, weil der Stand aus dem Protokoll neu entsteht.
//
// Abhängigkeiten werden hineingereicht: fsrsLib (ts-fsrs), store (lokaler Speicher), remote (Server, z. B. Supabase).

// ---------- Planer: dieselben Einstellungen wie die App (und Ultreya) ----------
export function makeScheduler(F, retention = 0.9) {
  const cache = {};
  const sched = g => cache[g === 1 ? 'kurz' : 'tage'] || (cache[g === 1 ? 'kurz' : 'tage'] = F.fsrs(F.generatorParameters(g === 1
    ? { request_retention: retention, enable_fuzz: true, enable_short_term: true, learning_steps: ['10m'], relearning_steps: ['10m'] }
    : { request_retention: retention, enable_fuzz: true, enable_short_term: false })));
  return { next: (card, at, g) => sched(g).next(card, at, g).card, empty: at => F.createEmptyCard(at) };
}

export const keyOf = e => `${e.deck}|${e.unit_id}|${e.direction}`;
const order = (a, b) => (a.reviewed_at - b.reviewed_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

// Lernstand aus Ereignissen. Ergebnis: Map key -> { card, events } (card im ts-fsrs-Format mit Date-Objekten).
export function replay(events, scheduler) {
  const byKey = new Map();
  for (const e of [...events].sort(order)) {
    const k = keyOf(e);
    const cur = byKey.get(k) || { card: scheduler.empty(new Date(e.reviewed_at)), events: 0 };
    cur.card = scheduler.next(cur.card, new Date(e.reviewed_at), e.rating);
    cur.events += 1;
    byKey.set(k, cur);
  }
  return byKey;
}

// ---------- Lokales Protokoll mit Warteschlange ----------
export class SyncEngine {
  /**
   * store:  { get(key) -> string|null, set(key, string) }   (localStorage oder ein Ersatz)
   * remote: { insertEvents(events) -> Promise, fetchEvents(cursor) -> Promise<{events, cursor}> }
   */
  // prefix/timeField: dieselbe Mechanik für das Protokoll des Kartenablaufs C1 (learning_events_v2: Präfix sentenzo.v2, Zeitfeld occurred_at)
  // chunk: Protokoll in Abschnitten zu je chunk Ereignissen speichern (log.0, log.1 …, Zahl in logc). Dann schreibt jedes neue Ereignis nur den
  // letzten Abschnitt neu statt des ganzen Protokolls (für das ausführlichere C1-Protokoll; das alte Protokoll bleibt beim Format „log“).
  constructor({ store, remote, deviceId, userKey = 'anon', overlapMs = 5 * 60 * 1000, prefix = 'sentenzo.sync', timeField = 'reviewed_at', chunk = 0 }) {
    this.store = store; this.remote = remote; this.deviceId = deviceId; this.overlapMs = overlapMs; this.timeField = timeField; this.chunk = chunk;
    this.k = name => `${prefix}.${userKey}.${name}`;
    if (chunk) {
      const n = Number(store.get(this.k('logc')) || 0);
      this.log = [];
      for (let i = 0; i < n; i++) this.log.push(...JSON.parse(store.get(this.k('log.' + i)) || '[]'));
      this._saved = this.log.length;
    } else this.log = JSON.parse(store.get(this.k('log')) || '[]');            // alle bekannten Ereignisse (eigene und von anderen Geräten)
    this.outbox = JSON.parse(store.get(this.k('outbox')) || '[]');      // IDs, die der Server noch nicht bestätigt hat
    this.cursor = store.get(this.k('cursor'));                           // Ankunftszeit auf dem Server, bis zu der abgeholt wurde
    this.lastSyncAt = Number(store.get(this.k('lastSyncAt')) || 0);
    this.ids = new Set(this.log.map(e => e.id));
  }

  persist() {
    if (this.chunk) {   // nur die Abschnitte ab dem ersten noch nicht gespeicherten Ereignis
      const c = this.chunk, from = Math.floor(Math.min(this._saved, this.log.length) / c), n = Math.ceil(this.log.length / c);
      for (let i = from; i < n; i++) this.store.set(this.k('log.' + i), JSON.stringify(this.log.slice(i * c, (i + 1) * c)));
      this.store.set(this.k('logc'), String(n));
      this._saved = this.log.length;
    } else this.store.set(this.k('log'), JSON.stringify(this.log));
    this.store.set(this.k('outbox'), JSON.stringify(this.outbox));
    if (this.cursor) this.store.set(this.k('cursor'), this.cursor);
    this.store.set(this.k('lastSyncAt'), String(this.lastSyncAt));
  }

  // Eine Bewertung festhalten: zuerst lokal (sofort), dann in die Warteschlange.
  record({ deck, unit_id, direction, sentence_id, rating, reviewed_at, id }) {
    const e = { id: id || cryptoId(), deck, unit_id, direction, sentence_id, rating, reviewed_at, device_id: this.deviceId };
    this.log.push(e); this.ids.add(e.id); this.outbox.push(e.id);
    this.persist();
    return e;
  }

  // Ein fertiges Ereignis mit fester ID festhalten (Kartenablauf C1). Gleiche ID ein zweites Mal: nichts passiert (idempotent).
  // Leere Felder werden nicht gespeichert (der lokale Speicher ist knapp, auf dem Server sind sie null).
  add(e) {
    if (this.ids.has(e.id)) return null;
    const ev = { device_id: this.deviceId };
    for (const [k, v] of Object.entries(e)) if (v !== null && v !== undefined && v !== false) ev[k] = v;
    this.log.push(ev); this.ids.add(ev.id); this.outbox.push(ev.id);
    this.persist();
    return ev;
  }

  // Warteschlange hochladen. Erst nach bestätigter Antwort werden IDs aus der Warteschlange genommen.
  async push(batchSize = 200) {
    let sent = 0;
    while (this.outbox.length) {
      const batch = this.outbox.slice(0, batchSize).map(id => this.log.find(e => e.id === id)).filter(Boolean);
      await this.remote.insertEvents(batch);            // wirft bei Fehler; dann bleibt alles in der Warteschlange
      const done = new Set(batch.map(e => e.id));
      this.outbox = this.outbox.filter(id => !done.has(id));
      sent += batch.length;
      this.persist();
    }
    return sent;
  }

  // Ereignisse anderer Geräte abholen. Mit Überlappung, weil Ankunftszeiten parallel schreibender Geräte nicht streng geordnet sind; Dubletten fallen über die ID heraus.
  async pull() {
    const since = this.cursor ? new Date(new Date(this.cursor).getTime() - this.overlapMs).toISOString() : null;
    let added = 0, cursor = since, more = true;
    while (more) {
      const res = await this.remote.fetchEvents(cursor);
      for (const e of res.events) {
        if (!this.ids.has(e.id)) { this.log.push(stripServerFields(e, this.timeField)); this.ids.add(e.id); added++; }
      }
      more = res.events.length > 0 && res.cursor && res.cursor !== cursor;
      if (res.cursor && (!this.cursor || res.cursor > this.cursor)) this.cursor = res.cursor;
      cursor = res.cursor;
      // Seitenweise speichern und warten, bis der Speicher es fest abgelegt hat (store.flush, IndexedDB): Alles auf einmal ließ das
      // localStorage-Journal bei langen Geschichten überlaufen (18.000 Ereignisse, „Fehler beim Sichern“). Der Lesestand wandert mit.
      this.persist();
      if (this.store.flush) await this.store.flush();
    }
    return added;
  }

  async sync() {
    const pushed = await this.push();
    const pulled = await this.pull();
    this.lastSyncAt = Date.now(); this.persist();
    return { pushed, pulled };
  }

  status() {
    return { pending: this.outbox.length, lastSyncAt: this.lastSyncAt, events: this.log.length, label: this.outbox.length ? 'wird gesichert' : 'gesichert' };
  }
}

function stripServerFields(e, timeField = 'reviewed_at') {
  const { user_id, inserted_at, app_version, ...rest } = e;
  if (typeof rest[timeField] === 'string') rest[timeField] = new Date(rest[timeField]).getTime();
  if (timeField !== 'reviewed_at') for (const k of Object.keys(rest)) if (rest[k] === null || rest[k] === false) delete rest[k];   // wie lokal gespeichert
  return rest;
}

export function cryptoId() {
  const c = globalThis.crypto;
  if (c && c.randomUUID) return c.randomUUID();
  // Rückfall für sehr alte Browser
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => { const r = Math.random() * 16 | 0; return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16); });
}

// ---------- Server-Anbindung für das Protokoll des Kartenablaufs C1 (Tabelle learning_events_v2) ----------
// columns: erlaubte Spalten (app/src/c1-events.js, V2_COLUMNS); alles andere wird nicht gesendet. Abgeholt wird je Deck und Policy-Version
// (eigene Einschreibung je Pilot: c1 und a1 deuten einander nie um).
export function supabaseEventsRemote(client, { table = 'learning_events_v2', deck, policy, columns, userId, appVersion } = {}) {
  return {
    async insertEvents(events) {
      const rows = events.map(e => {
        const r = {};
        for (const c of columns) if (e[c] !== undefined) r[c] = e[c];
        r.occurred_at = new Date(e.occurred_at).toISOString();
        if (userId) r.user_id = userId;
        if (appVersion) r.app_version = appVersion;
        return r;
      });
      const { error } = await client.from(table).upsert(rows, { onConflict: 'id', ignoreDuplicates: true });
      if (error) throw error;
    },
    async fetchEvents(cursor) {
      let q = client.from(table).select('*').eq('deck', deck).eq('policy_version', policy).order('inserted_at', { ascending: true }).limit(1000);
      if (cursor) q = q.gt('inserted_at', cursor);
      const { data, error } = await q;
      if (error) throw error;
      return { events: data, cursor: data.length ? data[data.length - 1].inserted_at : cursor };
    },
  };
}

// ---------- Server-Anbindung für Supabase (supabase-js v2) ----------
// userId ist nötig: review_events.user_id ist Pflicht und die Zeilenregel prüft auth.uid() = user_id (die Ereignisse im Protokoll tragen keine user_id).
// appVersion landet in review_events.app_version. Getestet mit einem nachgebauten Supabase in app/test (Vitest), noch nicht gegen das echte Projekt.
export function supabaseRemote(client, deck, { userId, appVersion } = {}) {
  return {
    async insertEvents(events) {
      const rows = events.map(e => ({ ...e, reviewed_at: new Date(e.reviewed_at).toISOString(), ...(userId ? { user_id: userId } : {}), ...(appVersion ? { app_version: appVersion } : {}) }));
      const { error } = await client.from('review_events').upsert(rows, { onConflict: 'id', ignoreDuplicates: true });
      if (error) throw error;
    },
    async fetchEvents(cursor) {
      let q = client.from('review_events').select('*').eq('deck', deck).order('inserted_at', { ascending: true }).limit(1000);
      if (cursor) q = q.gt('inserted_at', cursor);
      const { data, error } = await q;
      if (error) throw error;
      return { events: data, cursor: data.length ? data[data.length - 1].inserted_at : cursor };
    },
  };
}
